import { Customer, CustomerType } from "@/types/customerType";
import { CompanyTypeValues, CustomerTypeValues } from "@/constants/customer";
import { GeneralEnumeration } from "@/types/enumerations";
import { Process, Segment, Contract } from "@/types/contract";
import { executivePersonMainReferenceType } from "@/constants/executivePerson";
import { t } from "i18next";
import { isS3PreviousCustomer } from "./contract";
import type { PreviousCustomer } from "@/types/schemas/contract";

export const getMappedCustomerType = (customerType: CustomerType) => {
  if (customerType === "household") {
    return {
      customerType: CustomerTypeValues.Household,
      companyType: null,
      company: "",
    };
  }
  if (customerType === "tradesman") {
    return {
      customerType: CustomerTypeValues.Company,
      companyType: CompanyTypeValues.Tradesman,
      company: null,
    };
  }
  return {
    customerType: CustomerTypeValues.Company,
    companyType: CompanyTypeValues.Company,
    company: "",
  };
};

export const getCustomerTypeString = (contract: Contract) => {
  if (contract.customer.customerType === CustomerTypeValues.Household) {
    return "household";
  }
  if (contract.customer.companyType === CompanyTypeValues.Tradesman) {
    return "tradesman";
  }
  return "company";
};

export const isCustomerTypeSame = (customerType: CustomerType, customer: Customer) => {
  const customerMapped = getMappedCustomerType(customerType);
  return customer.customerType === customerMapped.customerType && customer.companyType === customerMapped.companyType;
};

export const isHousehold = (customer: Customer) => customer.customerType === CustomerTypeValues.Household;

export const isPreviousCustomerHousehold = (previousCustomer: PreviousCustomer) =>
  previousCustomer.customerType === CustomerTypeValues.Household;

export const isCompany = (customer: Customer) => !isHousehold(customer) && customer.companyType === CompanyTypeValues.Company;
export const isPreviousCustomerCompany = (previousCustomer: PreviousCustomer) =>
  previousCustomer.customerType === CustomerTypeValues.Company;

export const isTradesman = (customer: Customer) => !isHousehold(customer) && !isCompany(customer);

export const isCustomerPrefilled = (customer: Customer) => !!customer.customerId;

export const getPersonNameWithDegrees = (
  name: string,
  surname: string,
  degreeBeforeId: string | null,
  degreeBeforeId2: string | null,
  degreeAfterId: string | null,
  degreeBefores: GeneralEnumeration[],
  degreeAfters: GeneralEnumeration[]
) => {
  if (!name && !surname) {
    return "";
  }
  const degreeBeforeName = degreeBeforeId === null ? null : degreeBefores.find(degree => degree.id === degreeBeforeId)?.name;
  const degreeSecondBeforeName =
    degreeBeforeId2 === null ? null : degreeBefores.find(degree => degree.id === degreeBeforeId2)?.name;
  const degreeAfterName = degreeAfterId === null ? null : degreeAfters.find(degree => degree.id === degreeAfterId)?.name;
  const degreeBefore = degreeBeforeName ? `${degreeBeforeName} ` : "";
  const degreeSecondBefore = degreeSecondBeforeName ? `${degreeSecondBeforeName} ` : "";
  const degreeAfterWithComma = degreeAfterName ? `, ${degreeAfterName}` : "";
  return `${degreeBefore}${degreeSecondBefore}${name} ${surname}${degreeAfterWithComma}`;
};

export const getPreviousCustomerContactInfo = (contract: Contract) => ({
  isExecutivePerson: false,
  email: contract.previousCustomer?.contact.email ?? "",
  mobilePhone: contract.previousCustomer?.contact.mobilePhone ?? "",
  mobileCountryCode: contract.previousCustomer?.contact.mobileCountryCode,
});

export const getContactInfo = (contract: Contract) => {
  if (isS3PreviousCustomer(contract) && contract.previousCustomer) {
    return {
      isExecutivePerson: false,
      email: contract.previousCustomer.contact.email,
      mobilePhone: contract.previousCustomer.contact.mobilePhone,
      mobileCountryCode: contract.previousCustomer.contact.mobileCountryCode,
    };
  }

  if (isCompany(contract.customer) && (contract.process === Process.VRB || contract.segment === Segment.B2B)) {
    const mainExecutivePerson = contract.executivePeople.find(e => e.referenceType === executivePersonMainReferenceType);
    return {
      isExecutivePerson: true,
      email: mainExecutivePerson?.email ?? "",
      mobilePhone: mainExecutivePerson?.mobilePhone ?? "",
      mobileCountryCode: mainExecutivePerson?.mobileCountryCode,
    };
  }

  return {
    isExecutivePerson: false,
    email: contract.customer.contact.email,
    mobilePhone: contract.customer.contact.mobilePhone,
    mobileCountryCode: contract.customer.contact.mobileCountryCode,
  };
};

export const getCustomerTypeTranslation = (contract: Contract): string => {
  if (contract.customer.customerType === CustomerTypeValues.Household) {
    return t("common:household");
  }
  if (contract.customer.companyType === CompanyTypeValues.Tradesman) {
    return t("common:tradesman");
  }
  return t("common:company");
};
